import type { ReactNode } from "react";
import { genieTools } from "@/lib/genie/registries/tool-registry";
import { isRecord } from "@/utils/isRecord";
import type { ToolPartBase } from "../ToolComponents";
import {
  getApprovalDecision,
  isActionablePendingApproval,
  toolNameFromPartType,
} from "./approvalGate";
import {
  rowTarget,
  viewFromApproval,
  type ApprovalView,
  type EntityRef,
} from "./approvalView";
import {
  parseWorkflowApprovalPlan,
  workflowPlanSummary,
} from "./workflowApprovalActions";
import { WorkflowApprovalBody } from "./WorkflowApprovalBody";
import { NewTriggerBody } from "./NewTriggerBody";
import { FileImportBody } from "./FileImportBody";
import {
  EntityApprovalBody,
  ResultList,
  readToolApproval,
  type CardStage,
} from "./EntityApprovalBody";
import { ArgsApprovalBody, parseArgsApprovalPlan } from "./ArgsApprovalBody";
import {
  AgentChangesBody,
  CapabilityPinsBody,
  NewAgentBody,
} from "./AgentChangesBody";

/**
 * What an approval card shows for a call. The card, the composer, and the
 * header label all read it through ``resolveApproval``, so how a tool's card
 * looks is declared once, on the tool: a backend tool's ``ToolApproval`` is
 * stamped on the call, a Genie frontend tool declares one on its display.
 */
export interface ResolvedApproval {
  summary(): string | null;
  hasBody: boolean;
  body(): ReactNode;
}

function viewOf(part: ToolPartBase, toolName: string): ApprovalView {
  const noxus = part.callProviderMetadata?.noxus;
  // A third-party tool its server marks destructive declares nothing, and
  // still gets the warning.
  const approval =
    readToolApproval(noxus?.approval_card) ??
    genieTools.get(toolName)?.display?.approval ??
    {};
  const created =
    part.state === "output-available" &&
    isRecord(part.output) &&
    typeof part.output.id === "string"
      ? part.output.id
      : undefined;
  return viewFromApproval(
    approval,
    isRecord(part.input) ? part.input : {},
    noxus?.always_allow === false,
    created
  );
}

function cardStage(part: ToolPartBase): CardStage {
  if (isActionablePendingApproval(part)) return "pending";
  return getApprovalDecision(part)?.approved === false ? "denied" : "ran";
}

// Where the row's Open link goes once the call ran.
export function rowTargetOf(part: ToolPartBase): EntityRef | undefined {
  if (part.state !== "output-available") return undefined;
  if (cardStage(part) !== "ran") return undefined;
  return rowTarget(viewOf(part, toolNameFromPartType(part.type)), {
    input: part.input,
    output: part.output,
  });
}

export function declaresCard(part: ToolPartBase): boolean {
  return (
    readToolApproval(part.callProviderMetadata?.noxus?.approval_card) !== null
  );
}

export function resolveApproval(part: ToolPartBase): ResolvedApproval | null {
  const toolName = toolNameFromPartType(part.type);
  const view = viewOf(part, toolName);

  if (view?.renderer === "workflow_plan") {
    const plan = parseWorkflowApprovalPlan(toolName, part.input);
    if (plan) {
      return {
        summary: () => workflowPlanSummary(plan),
        hasBody: plan.actions.length > 0,
        body: () => (
          <>
            <WorkflowApprovalBody plan={plan} />
            {!isActionablePendingApproval(part) && (
              <ResultList view={view} output={part.output} input={part.input} />
            )}
          </>
        ),
      };
    }
  }

  if (view?.renderer === "agent_changes") {
    return {
      summary: () => null,
      hasBody: true,
      body: () => <AgentChangesBody view={view} part={part} />,
    };
  }

  if (view?.renderer === "new_agent") {
    return {
      summary: () => null,
      hasBody: true,
      body: () => <NewAgentBody view={view} part={part} />,
    };
  }

  if (view?.renderer === "file_import") {
    return {
      summary: () => null,
      hasBody: true,
      body: () => <FileImportBody view={view} part={part} />,
    };
  }

  if (view?.renderer === "new_trigger") {
    return {
      summary: () => null,
      hasBody: true,
      body: () => <NewTriggerBody view={view} part={part} />,
    };
  }

  if (view?.renderer === "capability_pins") {
    return {
      summary: () => null,
      hasBody: true,
      body: () => <CapabilityPinsBody view={view} part={part} />,
    };
  }

  if (view?.op) {
    return {
      summary: () => null,
      hasBody: true,
      body: () => (
        <EntityApprovalBody
          view={view}
          input={part.input}
          output={part.output}
          stage={cardStage(part)}
        />
      ),
    };
  }

  // A tool that declares no op (a third-party MCP tool an owner gated, or a
  // gated tool with nothing better to show) lists its args.
  const args = parseArgsApprovalPlan(toolName, part.input);
  if (!args && !view?.warning) return null;
  return {
    summary: () => null,
    hasBody: true,
    body: () => <ArgsApprovalBody plan={args} warning={view?.warning} />,
  };
}
